import type { TruncationMode } from '../../../../../typography/text-ellipsis/TextEllipsis.js';
import type { TextMeasurer } from '../../../../core/utils/text-measure.js';
import {
  HONEYCOMB_LABEL_FONT_SIZE,
  HONEYCOMB_VALUE_LABEL_FONT_SIZE,
} from '../../../constants.js';
import { CanvasTextAreaProps } from '../../../types/canvas-text-area.js';

const ELLIPSIS = '…';

const truncateEnd = (
  text: string,
  charWidths: number[],
  capacity: number,
): string => {
  let kept = '';
  let usedWidth = 0;

  for (let i = 0; i < charWidths.length; i++) {
    if (usedWidth + charWidths[i] > capacity) {
      break;
    }
    kept += text[i];
    usedWidth += charWidths[i];
  }

  return kept + ELLIPSIS;
};

const truncateStart = (
  text: string,
  charWidths: number[],
  capacity: number,
): string => {
  let kept = '';
  let usedWidth = 0;

  for (let i = charWidths.length - 1; i >= 0; i--) {
    if (usedWidth + charWidths[i] > capacity) {
      break;
    }
    kept = text[i] + kept;
    usedWidth += charWidths[i];
  }

  return ELLIPSIS + kept;
};

const truncateMiddle = (
  text: string,
  charWidths: number[],
  capacity: number,
): string => {
  let head = '';
  let headWidth = 0;
  let tail = '';
  let tailWidth = 0;
  let lo = 0;
  let hi = charWidths.length - 1;
  let pickHead = true;

  while (lo <= hi) {
    if (pickHead) {
      if (headWidth + charWidths[lo] + tailWidth > capacity) {
        break;
      }
      head += text[lo];
      headWidth += charWidths[lo++];
    } else {
      if (headWidth + charWidths[hi] + tailWidth > capacity) {
        break;
      }
      tail = text[hi] + tail;
      tailWidth += charWidths[hi--];
    }
    pickHead = !pickHead;
  }

  return head + ELLIPSIS + tail;
};

const truncateWithCanvas = (
  measureTextWidth: (text: string) => number,
  text: string,
  maxWidth: number,
  mode: TruncationMode,
): string => {
  const ellipsisWidth = measureTextWidth(ELLIPSIS);
  const capacity = maxWidth - ellipsisWidth;
  if (capacity <= 0) {
    return '';
  }

  const charWidths = Array.from(text).map((ch) => measureTextWidth(ch));

  switch (mode) {
    case 'end':
      return truncateEnd(text, charWidths, capacity);
    case 'start':
      return truncateStart(text, charWidths, capacity);
    default:
      return truncateMiddle(text, charWidths, capacity);
  }
};

export const getTruncationMethod =
  (
    textArea: CanvasTextAreaProps,
    text: string,
    truncationMode: TruncationMode,
    measure: TextMeasurer,
    contentType: 'value' | 'label' = 'label',
    fontSize?: number,
  ) =>
  (): string => {
    const resolvedFontSize =
      fontSize ??
      (contentType === 'value'
        ? HONEYCOMB_VALUE_LABEL_FONT_SIZE
        : HONEYCOMB_LABEL_FONT_SIZE);
    const measureTextWidth = (textValue: string) =>
      measure(textValue, resolvedFontSize);

    if (measureTextWidth(text) <= textArea.width) {
      return text;
    }
    return truncateWithCanvas(
      measureTextWidth,
      text,
      textArea.width,
      truncationMode,
    );
  };
